import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Sparkles,
  Wrench,
  Trees,
  Sofa,
  Shirt,
  Cog,
  CheckCircle2,
  Building2,
  Landmark,
  BadgeCheck,
} from "lucide-react";
import { IMAGES, CAPABILITIES, TRUST_ITEMS, INDUSTRIES, CERTIFICATIONS } from "../data/site";
import SectionHeader from "../components/SectionHeader";
import CTABanner from "../components/CTABanner";

const ICONS = {
  "facility-support": Sparkles,
  "grounds-exterior": Trees,
  "mechanical-trade": Wrench,
  "commercial-equipment": Cog,
  "furnishings-interior": Sofa,
  "laundry-specialty": Shirt,
  "custom-contract": ShieldCheck,
};

function Hero() {
  return (
    <section className="relative bg-[#0A1F35] text-white overflow-hidden" data-testid="home-hero">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${IMAGES.heroBuilding})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.32,
        }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(115deg, rgba(10,31,53,0.95) 0%, rgba(10,31,53,0.78) 55%, rgba(10,31,53,0.65) 100%)",
        }}
        aria-hidden="true"
      />
      <div className="absolute top-0 left-0 h-full w-px bg-white/10" />
      <div className="absolute top-0 left-1/2 h-full w-px bg-white/5 hidden lg:block" />

      <div className="relative container-x py-24 sm:py-32 lg:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <p className="text-overline text-[#9BB7A2] mb-6 reveal">
              <span className="inline-block h-px w-8 bg-[#9BB7A2] align-middle mr-3" />
              Government · Commercial · Institutional
            </p>
            <h1 className="heading-hero text-4xl sm:text-5xl lg:text-[4.25rem] mb-7 reveal reveal-delay-1">
              Reliable Multi-Capability Services for{" "}
              <span className="text-[#9BB7A2]">Government, Commercial,</span> and Institutional Clients.
            </h1>
            <p className="text-lg sm:text-xl text-white/80 leading-relaxed max-w-2xl reveal reveal-delay-2">
              Street Society delivers professional service solutions across facilities, grounds,
              equipment, and operational support environments — built on discipline, accountability,
              and contract-ready execution.
            </p>
            <div className="mt-9 flex flex-wrap gap-4 reveal reveal-delay-3">
              <Link to="/capabilities" className="btn-base btn-secondary" data-testid="hero-cta-capabilities">
                View Capabilities <ArrowRight size={16} />
              </Link>
              <Link to="/contact" className="btn-base btn-outline-light" data-testid="hero-cta-contact">
                Contact Us
              </Link>
            </div>
          </div>

          <div className="lg:col-span-4 hidden lg:block reveal reveal-delay-4">
            <div className="border border-white/15 bg-white/[0.04] backdrop-blur-sm p-7">
              <p className="text-overline text-[#9BB7A2] mb-5">At a Glance</p>
              <ul className="space-y-4 text-sm">
                {[
                  ["7", "Core Capability Sectors"],
                  ["6", "Active NAICS Codes"],
                  ["6", "Certifications & Designations"],
                  ["1", "SAM.gov Registered Entity"],
                ].map(([num, label]) => (
                  <li key={label} className="flex items-baseline gap-4 border-b border-white/10 last:border-0 pb-3 last:pb-0">
                    <span className="font-serif text-3xl text-white">{num}</span>
                    <span className="text-white/75">{label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustBar() {
  return (
    <section
      className="bg-white border-y border-[#E2E8F0]"
      data-testid="trust-bar"
    >
      <div className="container-x py-6 grid grid-cols-2 lg:grid-cols-4 gap-y-5 gap-x-8">
        {TRUST_ITEMS.map((item, idx) => (
          <div
            key={item.label}
            className={`flex flex-col gap-1 ${
              idx < TRUST_ITEMS.length - 1 ? "lg:border-r lg:border-[#E2E8F0] lg:pr-8" : ""
            }`}
          >
            <span className="text-overline text-[#4B7055]">{item.label}</span>
            <span className="font-serif text-lg text-[#0A1F35] font-bold tracking-tight">
              {item.value}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

function Capabilities() {
  return (
    <section className="section-y bg-[#F5F7F9]" data-testid="home-capabilities">
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-8">
            <SectionHeader
              eyebrow="Core Capabilities"
              title="Seven service sectors. One disciplined operator."
              intro="Street Society delivers across a broad operational footprint — built to support agencies, primes, facility operators, and commercial clients with consistent execution and clear accountability."
            />
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link to="/capabilities" className="btn-base btn-outline-dark" data-testid="capabilities-view-all">
              View All Capabilities <ArrowRight size={16} />
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
          {CAPABILITIES.map((cap) => {
            const Icon = ICONS[cap.id] || ShieldCheck;
            return (
              <Link
                key={cap.id}
                to={`/capabilities#${cap.id}`}
                className="group bg-white p-8 lg:p-9 flex flex-col gap-5 card-hover relative"
                data-testid={`capability-card-${cap.id}`}
              >
                <div className="flex items-center justify-between">
                  <span className="inline-flex h-12 w-12 items-center justify-center bg-[#0A1F35] text-white group-hover:bg-[#4B7055] transition-colors">
                    <Icon size={20} strokeWidth={1.75} />
                  </span>
                  <span className="text-overline text-[#9CA5B0] group-hover:text-[#4B7055] transition-colors">
                    {String(CAPABILITIES.indexOf(cap) + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-serif text-xl text-[#0A1F35] leading-snug font-bold">
                  {cap.title}
                </h3>
                <p className="text-sm text-[#4A5568] leading-relaxed flex-1">{cap.short}</p>
                <span className="flex items-center gap-2 text-xs font-semibold text-[#0A1F35] group-hover:text-[#4B7055] transition-colors">
                  Learn more <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function WhyStreetSociety() {
  const pillars = [
    {
      icon: ShieldCheck,
      title: "Operational Discipline",
      desc: "Standard operating procedures, supervision, and documentation built for contract environments.",
    },
    {
      icon: BadgeCheck,
      title: "Compliance Awareness",
      desc: "Federal, state, and local procurement standards inform how we plan, staff, and report.",
    },
    {
      icon: CheckCircle2,
      title: "Responsive Execution",
      desc: "Clear points of contact, defined response windows, and accountable field coordination.",
    },
    {
      icon: Building2,
      title: "Multi-Site Scalability",
      desc: "Structured to support recurring service across campuses, agencies, and facility portfolios.",
    },
  ];
  return (
    <section className="section-y bg-white" data-testid="why-street-society">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-28">
          <SectionHeader
            eyebrow="Why Street Society"
            title="Built to perform inside contract environments."
            intro="Street Society is structured to support government, commercial, and institutional clients with predictable execution and the operational posture procurement teams expect."
          />
          <div className="mt-8 relative overflow-hidden">
            <img
              src={IMAGES.team}
              alt="Professional field team"
              className="w-full h-64 object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-[#0A1F35]/20" />
          </div>
        </div>

        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
          {pillars.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                className="border border-[#E2E8F0] p-7 card-hover bg-white"
                data-testid={`why-pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <span className="inline-flex h-11 w-11 items-center justify-center bg-[#F5F7F9] text-[#0A1F35] mb-5">
                  <Icon size={20} strokeWidth={1.75} />
                </span>
                <h3 className="font-serif text-lg font-bold text-[#0A1F35] mb-2">{p.title}</h3>
                <p className="text-sm text-[#4A5568] leading-relaxed">{p.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function IndustriesPreview() {
  return (
    <section className="section-y bg-[#0A1F35] text-white relative overflow-hidden" data-testid="home-industries">
      <div
        className="absolute inset-0 opacity-15"
        style={{
          backgroundImage: `url(${IMAGES.campus})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A1F35]/95 via-[#0A1F35]/92 to-[#0A1F35]" aria-hidden="true" />
      <div className="relative container-x">
        <div className="max-w-3xl">
          <p className="text-overline text-[#9BB7A2] mb-4">Industries Served</p>
          <h2 className="heading-section text-3xl sm:text-4xl lg:text-5xl text-white">
            Trusted across public, commercial, and institutional environments.
          </h2>
        </div>

        <div className="mt-12 grid grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
          {INDUSTRIES.slice(0, 9).map((ind, i) => (
            <div
              key={ind.title}
              className="bg-[#0A1F35] p-7 flex flex-col gap-3 hover:bg-[#15304A] transition-colors"
              data-testid={`industry-preview-${i}`}
            >
              <span className="text-overline text-[#9BB7A2]">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="font-serif text-lg text-white font-bold leading-snug">{ind.title}</h3>
              <p className="text-sm text-white/70 leading-relaxed">{ind.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex justify-start">
          <Link to="/industries-served" className="btn-base btn-outline-light" data-testid="industries-view-all">
            All Industries <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function GovReadiness() {
  return (
    <section className="section-y bg-white" data-testid="gov-readiness">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <p className="text-overline text-[#4B7055] mb-4">Government Contracting Readiness</p>
          <h2 className="heading-section text-3xl sm:text-4xl lg:text-5xl text-[#0A1F35]">
            SAM.gov registered. Procurement-ready. Built for compliance environments.
          </h2>
          <p className="mt-5 text-[#4A5568] text-base sm:text-lg leading-relaxed">
            Street Society LLC is positioned to support federal, state, local, and commercial procurement
            needs through responsive service delivery, compliance awareness, and dependable contract execution.
          </p>

          <ul className="mt-8 space-y-3">
            {[
              "Contract readiness and documented operating procedures",
              "Compliance-focused field operations",
              "Prime contractor teaming and subcontracting support",
              "Capability statement available on request",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm text-[#1A2530]">
                <CheckCircle2 size={18} className="text-[#4B7055] mt-0.5 shrink-0" strokeWidth={2} />
                {item}
              </li>
            ))}
          </ul>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/government-contracting" className="btn-base btn-primary" data-testid="gov-readiness-cta">
              Government Contracting <ArrowRight size={16} />
            </Link>
            <Link to="/past-performance" className="btn-base btn-outline-dark" data-testid="gov-readiness-past">
              Past Performance
            </Link>
          </div>
        </div>

        <div className="lg:col-span-6 order-1 lg:order-2">
          <div className="border border-[#E2E8F0] bg-[#F5F7F9] p-8 lg:p-10">
            <div className="flex items-center gap-3 pb-5 border-b border-[#E2E8F0]">
              <Landmark size={22} className="text-[#0A1F35]" />
              <p className="text-overline text-[#0A1F35]">Entity Snapshot</p>
            </div>
            <dl className="mt-6 grid grid-cols-2 gap-y-5 gap-x-6">
              {[
                ["UEI", "FGXGDXF8DGT7"],
                ["CAGE", "8DD93"],
                ["Status", "SAM.gov Registered"],
                ["Location", "United States"],
              ].map(([k, v]) => (
                <div key={k}>
                  <dt className="text-overline text-[#4A5568]">{k}</dt>
                  <dd className="font-serif text-lg text-[#0A1F35] font-bold mt-1">{v}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-8 pt-6 border-t border-[#E2E8F0]">
              <p className="text-overline text-[#4B7055] mb-3">Certifications & Designations</p>
              <ul className="flex flex-wrap gap-2">
                {CERTIFICATIONS.map((c) => (
                  <li
                    key={c}
                    className="text-xs px-3 py-1.5 bg-white border border-[#E2E8F0] text-[#1A2530]"
                  >
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="page-home">
      <Hero />
      <TrustBar />
      <Capabilities />
      <WhyStreetSociety />
      <IndustriesPreview />
      <GovReadiness />
      <CTABanner />
    </div>
  );
}
